import React, { useState, useEffect } from "react";
import { FileDown, ChevronDown, ChevronUp, Star } from "lucide-react";

// Interface disesuaikan langsung dengan field yang dikembalikan backend
interface RekapPemotonganItem {
  id_karyawan: string;
  nama: string;
  pangkatGol: string;
  nip: string;
  npwp: string;
  statusPegawai: string;
  statusWP: string;
  uraianKelasJabatan: string;
  nilaiKelasJabatan: string | number;
  tunjanganJabatan: number;
  tunjanganStatus: number;
  totalTunjangan: number;
  totalAbsenPct: number;
  nominalAbsenRp: number;
  pemotonganSkp: number;
  jumlahDiterima: number;
}

/* ---------- Utility Format ---------- */
const rupiah = (n: number) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Math.round(n) || 0);

const pct = (n: number) => `${(Math.round(n * 100) / 100).toFixed(2)}%`;

export default function RekapanPengurangan() {
  const [periode, setPeriode] = useState(() => {
    const tanggal = new Date();
    const tahun = tanggal.getFullYear();
    const bulan = String(tanggal.getMonth() + 1).padStart(2, "0");
    const hari = String(tanggal.getDate()).padStart(2, "0");
    return `${tahun}-${bulan}-${hari}`;
  });
  const [employees, setEmployees] = useState<RekapPemotonganItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showDetail, setShowDetail] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/bawaslu_phwt/rekapan/pemotongan?tanggal=${periode}`)
      .then((res) => res.json())
      .then((result) => {
        const rawData = Array.isArray(result) ? result : result.data || [];
        setEmployees(rawData);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Gagal memuat data rekap pemotongan:", error);
        setLoading(false);
      });
  }, [periode]);

  const exportXlsx = async () => {
    const XLSX = await import("xlsx");

    // Build header rows to match table structure (3 header rows with grouped PEMOTONGAN)
    const cols = [
      "No.", "Nama", "Pangkat/Gol", "NIP", "NPWP", "Status Pegawai", "Status WP",
      "Uraian Kelas Jabatan", "Nilai Kls Jab", "Tunjangan Jabatan", "Nominal Absen", "Nominal SKP",
      "PEMOTONGAN", "", "", "", "Jumlah Diterima",
    ];

    const headerRow2 = new Array(cols.length).fill("");
    // ABSEN occupies cols 12-13 (0-based index 12..13), SKP occupies 14-15
    headerRow2[12] = "ABSEN";
    headerRow2[14] = "SKP";

    const headerRow3 = new Array(cols.length).fill("");
    headerRow3[12] = "%";
    headerRow3[13] = "Rp";
    headerRow3[14] = "%";
    headerRow3[15] = "Rp";

    const data = employees.map((e, i) => [
      i + 1,
      e.nama,
      e.pangkatGol,
      e.nip,
      e.npwp,
      e.statusPegawai,
      e.statusWP,
      e.uraianKelasJabatan,
      e.nilaiKelasJabatan,
      e.tunjanganJabatan,
      Math.round((e.tunjanganJabatan * (60 / 100)) * (e.totalAbsenPct / 100)), // nominal absen Rp
      Math.round((e.tunjanganJabatan * (40 / 100) * (e.pemotonganSkp / 100))), // nominal skp Rp
      e.totalAbsenPct, // pemotongan % (ABSEN)
      Math.round((e.tunjanganJabatan * (60 / 100)) * (e.totalAbsenPct / 100)), // ABSEN Rp repeated for clarity
      e.pemotonganSkp, // SKP %
      Math.round((e.tunjanganJabatan * (40 / 100) * (e.pemotonganSkp / 100))), // SKP Rp
      Math.round(
        (e.tunjanganJabatan * (60 / 100)) + 
        (e.tunjanganJabatan * (40 / 100)) - 
        ((e.tunjanganJabatan * (60 / 100)) * (e.totalAbsenPct / 100 )) - 
        ((e.tunjanganJabatan * (40 / 100) * (e.pemotonganSkp / 100)))
      ),
    ]);

    const aoa = [[`Rekapitulasi Pemotongan Tunjangan Kinerja Bulan ${periode}`], [], cols, headerRow2, headerRow3, ...data];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    // merges: title row across all columns
    const totalCols = cols.length;
    const merges: any[] = [];
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } });

    // merge first header row cells that have rowspan=3 (columns 0..11 and last col index 16)
    const rowspanCols = Array.from({ length: 12 }, (_, i) => i).concat([16]);
    rowspanCols.forEach((c) => merges.push({ s: { r: 2, c }, e: { r: 4, c } }));

    // merge PEMOTONGAN on header row (row index 2) spanning cols 12..15
    merges.push({ s: { r: 2, c: 12 }, e: { r: 2, c: 15 } });
    // merge ABSEN (row 3) cols 12..13 and SKP (row 3) cols 14..15
    merges.push({ s: { r: 3, c: 12 }, e: { r: 3, c: 13 } });
    merges.push({ s: { r: 3, c: 14 }, e: { r: 3, c: 15 } });

    ws["!merges"] = merges;

    // set column widths
    ws["!cols"] = cols.map((c) => ({ wch: Math.max(8, Math.min(30, String(c).length + 8)) }));

    // freeze panes: after header rows (title + empty + 3 header rows = rows 0..4), freeze at row 5 -> ySplit = 5
    ws["!freeze"] = { xSplit: 0, ySplit: 5 };

    // set numeric types for numeric cells
    for (let r = 5; r < aoa.length; r++) {
      const rowIndex = r;
      // numeric columns indices: 0(No),8(nilai),9(tunjanganJabatan),10(nominalAbsen),11(nominalSKP),12..15 percentages and rps,16 jumlah diterima
      const numericCols = [0, 8, 9, 10, 11, 12, 13, 14, 15, 16];
      numericCols.forEach((c) => {
        const addr = XLSX.utils.encode_cell({ r: rowIndex, c });
        const cell = ws[addr];
        if (!cell) return;
        if (cell.v === null || cell.v === undefined || cell.v === "") return;
        cell.t = "n";
      });
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rekap Pemotongan");
    XLSX.writeFile(wb, `Rekap_Pemotongan_${periode}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="max-w-8xl mx-auto">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center">
            <Star className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">Rekapan Pemotongan Tunjangan</h1>
          </div>

          <div className="flex gap-2">
            <input 
              type="date" 
              value={periode} 
              onChange={(e) => setPeriode(e.target.value)}
              className="border border-slate-300 rounded-xl px-3 py-1.5 text-xs bg-white"
            />
            {/* <button
              onClick={() => setShowDetail((s) => !s)}
              className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-100 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-200"
            >
              {showDetail ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showDetail ? "Sembunyikan Detail" : "Tampilkan Detail"}
            </button> */}
            <button
              onClick={exportXlsx}
              className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-yellow-400 to-amber-300 px-4 py-2 text-xs font-semibold text-slate-900 hover:from-yellow-300"
            >
              <FileDown size={14} />
              Ekspor Excel
            </button>
          </div>
        </div>

        <div className="relative mx-auto max-w-full rounded-lg border border-slate-200 bg-white shadow-sm overflow-x-auto">
          <table className="w-max min-w-full border-collapse text-[12px]">
            <thead>
              <tr className="bg-[#152544] text-white text-center">
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>No.</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Nama</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Pangkat/Gol</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>NIP</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>NPWP</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Status Pegawai</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Status WP</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Uraian Kelas Jabatan</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Nilai Kls Jab</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Tunjangan Jabatan</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Nominal Absen</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Nominal SKP</th>
                <th className="border border-slate-600 px-2 py-2 bg-amber-700" colSpan={4}>PEMOTONGAN</th>
                <th className="border border-slate-600 px-2 py-2" rowSpan={3}>Jumlah Diterima</th>
              </tr>
              <tr className="bg-[#1B2E52] text-[#C9D6EC] text-center">
                <th className="border border-slate-600 px-2 py-1 bg-amber-800" colSpan={2}>ABSEN</th>
                <th className="border border-slate-600 px-2 py-1 bg-amber-900" colSpan={2}>SKP</th>
              </tr>
              <tr className="bg-[#1B2E52] text-[#C9D6EC] text-center text-[10px]">
                <th className="border border-slate-600 px-2 py-1">%</th>
                <th className="border border-slate-600 px-2 py-1">Rp</th>
                <th className="border border-slate-600 px-2 py-1">%</th>
                <th className="border border-slate-600 px-2 py-1">Rp</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={13} className="py-8 text-center text-slate-500 italic">
                    Memuat data rekap pemotongan...
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan={13} className="py-8 text-center text-slate-500 italic">
                    Tidak ada data pegawai yang tersedia.
                  </td>
                </tr>
              ) : (
                employees.map((e, i) => (
                  <tr key={e.id_karyawan || i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                    <td className="border border-slate-200 px-2 py-1 text-center">{i + 1}</td>
                    <td className="border border-slate-200 px-2 py-1 font-medium">{e.nama || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.pangkatGol || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.nip || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.npwp || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.statusPegawai || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1">{Number(e.statusWP) === 0 ? "" : e.statusWP || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1">{e.uraianKelasJabatan || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1 text-center">{e.nilaiKelasJabatan || "—"}</td>
                    <td className="border border-slate-200 px-2 py-1 text-right font-semibold">{rupiah(e.tunjanganJabatan)}</td>
                    <td className="border border-slate-200 px-2 py-1 text-right font-semibold">{rupiah((e.tunjanganJabatan * (60/100)))}</td>
                    <td className="border border-slate-200 px-2 py-1 text-right font-semibold">{rupiah((e.tunjanganJabatan * (40/100)))}</td>
                    <td className="border border-slate-200 px-2 py-1 text-right font-semibold text-amber-700 bg-amber-50/50">
                      {pct(e.totalAbsenPct)}
                    </td>
                    <td className="border border-slate-200 px-2 py-1 text-right font-semibold text-amber-700 bg-amber-50/50">
                      {rupiah((e.tunjanganJabatan * (60/100)) * (e.totalAbsenPct / 100 ))}
                    </td>
                    <td className="border border-slate-200 px-2 py-1 text-right font-semibold text-red-600 bg-red-50/30">
                      {[pct(e.pemotonganSkp)]}
                    </td>
                    <td className="border border-slate-200 px-2 py-1 text-right font-bold text-emerald-700 bg-emerald-50/30">
                      {rupiah((e.tunjanganJabatan * (40/100) * (e.pemotonganSkp / 100)))}
                    </td>
                    <td className="border border-slate-200 px-2 py-1 text-right font-bold text-emerald-700 bg-emerald-50/30">
                      {rupiah(
                        (e.tunjanganJabatan * (60/100)) + 
                        (e.tunjanganJabatan * (40/100)) - 
                        ((e.tunjanganJabatan * (60/100)) * (e.totalAbsenPct / 100 )) - 
                        ((e.tunjanganJabatan * (40/100) * (e.pemotonganSkp / 100))
                      ))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}